/**
 * Shift service — the free-text co-writer.
 *
 * Calls POST /api/shift with the current progression and a plain-language
 * instruction ("darker", "more like the opening"). The backend replies with a
 * REVIEWABLE plan expressed in the moves vocabulary — never applied here. The
 * UI renders the plan as a keep/skip review queue (never auto-apply).
 *
 * Mirrors the auth + fetch conventions in api-service.ts (getAuthHeaders,
 * VITE_API_URL, guarded error-body parse); inlines the 401/422/429 handling so
 * this file stays self-contained.
 */
import { getAuthHeaders } from '@/services/api-service';
import type { Chord } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/** One reviewable step, expressed in the moves vocabulary. */
export interface ShiftPlanStep {
  moveId: string;
  index: number;
  rationale: string;
}

export interface ShiftResponse {
  plan: ShiftPlanStep[];
  summary: string;
}

/** Carries the HTTP status so the UI can pick a kind, specific handwriting line. */
export class ShiftError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ShiftError';
    this.status = status;
  }
}

function toShiftChord(c: Chord) {
  return {
    scaleDegree: c.scaleDegree,
    quality: c.quality,
    extensions: c.extensions,
    isChromatic: c.isChromatic,
    chromaticType: c.chromaticType,
    startBeat: c.startBeat,
    duration: c.duration,
  };
}

export async function requestShift(
  chords: Chord[],
  key: string,
  mode: string,
  instruction: string
): Promise<ShiftResponse> {
  const headers = await getAuthHeaders();
  const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);

  const response = await fetch(`${API_BASE_URL}/api/shift`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      chords: sorted.map(toShiftChord),
      key,
      mode,
      instruction,
    }),
  });

  if (response.status === 401) {
    throw new ShiftError("sign in to ask for a shift.", 401);
  }
  if (response.status === 429) {
    throw new ShiftError("that is a lot of asking at once — give it a minute.", 429);
  }
  if (response.status === 422) {
    throw new ShiftError("couldn't find a move for that — try different words.", 422);
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ShiftError(errorData.detail || "the shift didn't come through — try again.", response.status);
  }

  return response.json();
}
